import { KeyboardCode, getScrollableAncestors } from '@dnd-kit/core';
import { hasSortableData } from '@dnd-kit/sortable';
import { subtract } from '@dnd-kit/utilities';

const directions = [KeyboardCode.Right, KeyboardCode.Left];
const sequenceKeyboardCoordinates = (event, {
  context
}) => {
  const {
    active,
    droppableRects,
    droppableContainers
  } = context;
  if (!directions.includes(event.code)) {
    return;
  }
  event.preventDefault();
  const collisionRect = droppableContainers.get(active?.id)?.rect.current;
  if (!active || !collisionRect) {
    return;
  }
  const droppableContainerRects = droppableContainers.getEnabled().map(it => ({
    ...it,
    rect: {
      current: droppableRects.get(it.id) ?? null
    }
  })).filter(it => it && !it.disabled && it.id !== active.id && !!it.rect.current);
  const rectsBefore = droppableContainerRects.filter(it => collisionRect.top - it.rect.current.top > 5);
  const rectsOnSameLine = droppableContainerRects.filter(it => Math.abs(it.rect.current.top - collisionRect.top) < 5);
  const rectsAfter = droppableContainerRects.filter(it => collisionRect.top - it.rect.current.top < -5);
  switch (event.code) {
    case KeyboardCode.Left:
      {
        const rectsOnSameLineBeforeCurrent = rectsOnSameLine.filter(it => it.rect.current.right < collisionRect.left);
        if (rectsOnSameLineBeforeCurrent.length) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const nextRect = rectsOnSameLineBeforeCurrent.sort((a, b) => b.rect.current.left - a.rect.current.left)[0];
          return adjustOffset(nextRect.id, context);
        } else if (rectsBefore.length) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const nextRect = rectsBefore.sort((a, b) => {
            if (a.rect.current.top === b.rect.current.top) {
              return b.rect.current.left - a.rect.current.left;
            } else {
              return b.rect.current.top - a.rect.current.top;
            }
          })[0];
          return adjustOffset(nextRect.id, context);
        }
        return undefined;
      }
    case KeyboardCode.Right:
      {
        const rectsOnSameLineBeforeCurrent = rectsOnSameLine.filter(it => it.rect.current.left > collisionRect.right);
        if (rectsOnSameLineBeforeCurrent.length) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const nextRect = rectsOnSameLineBeforeCurrent.sort((a, b) => a.rect.current.left - b.rect.current.left)[0];
          return adjustOffset(nextRect.id, context);
        } else if (rectsAfter.length) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const nextRect = rectsAfter.sort((a, b) => {
            if (a.rect.current.top === b.rect.current.top) {
              return a.rect.current.left - b.rect.current.left;
            } else {
              return a.rect.current.top - b.rect.current.top;
            }
          })[0];
          return adjustOffset(nextRect.id, context);
        }
        return undefined;
      }
  }
};

/**
 * ported from @dnd-kit/sortable sortableKeyboardGetter
 */
function adjustOffset(collisionId, context) {
  const {
    active,
    collisionRect,
    droppableRects,
    droppableContainers,
    scrollableAncestors
  } = context;
  if (!collisionRect) {
    return undefined;
  }
  const activeDroppable = droppableContainers.get(active?.id);
  const newDroppable = droppableContainers.get(collisionId);
  const newRect = newDroppable ? droppableRects.get(newDroppable.id) : null;
  const newNode = newDroppable?.node.current;
  if (newNode && newRect && activeDroppable && newDroppable) {
    const newScrollAncestors = getScrollableAncestors(newNode);
    const hasDifferentScrollAncestors = newScrollAncestors.some((element, index) => scrollableAncestors[index] !== element);
    const hasSameContainer = isSameContainer(activeDroppable, newDroppable);
    const isAfterActive = isAfter(activeDroppable, newDroppable);
    const offset = hasDifferentScrollAncestors || !hasSameContainer ? {
      x: 0,
      y: 0
    } : {
      x: isAfterActive ? collisionRect.width - newRect.width : 0,
      y: isAfterActive ? collisionRect.height - newRect.height : 0
    };
    const rectCoordinates = {
      x: newRect.left,
      y: newRect.top
    };
    const newCoordinates = offset.x && offset.y ? rectCoordinates : subtract(rectCoordinates, offset);
    return newCoordinates;
  }
}
function isSameContainer(a, b) {
  if (!hasSortableData(a) || !hasSortableData(b)) {
    return false;
  }
  return a.data.current.sortable.containerId === b.data.current.sortable.containerId;
}
function isAfter(a, b) {
  if (!hasSortableData(a) || !hasSortableData(b)) {
    return false;
  }
  if (!isSameContainer(a, b)) {
    return false;
  }
  return a.data.current.sortable.index < b.data.current.sortable.index;
}

export { sequenceKeyboardCoordinates };
//# sourceMappingURL=util.keyboard-coordinates.js.map
